import { useState, useMemo } from 'react';
import { Download, RotateCcw } from 'lucide-react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import type { ErlangCInputs, DayPlannerInterval } from '../types/erlang';
import { DEFAULT_INTERVALS, calculateDayPlan } from '../utils/dayPlanner';
import { generateDayPlanReport, downloadReport } from '../utils/reportGenerator';
import MetricCard from './MetricCard';
import { InputField } from './ErlangCTab';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

export default function DayPlannerTab() {
  const [baseInputs, setBaseInputs] = useState<ErlangCInputs>({
    callsPerInterval: 350,
    ahtSeconds: 240,
    intervalMinutes: 30,
    targetSLPercent: 80,
    targetAnswerTime: 20,
    shrinkagePercent: 30,
    costPerAgentHour: 25,
  });

  const [intervals, setIntervals] = useState<DayPlannerInterval[]>([...DEFAULT_INTERVALS]);
  const [useErlangA, setUseErlangA] = useState(false);
  const [avgPatienceTime, setAvgPatienceTime] = useState(120);

  const summary = useMemo(
    () => calculateDayPlan(intervals, baseInputs, useErlangA, avgPatienceTime),
    [intervals, baseInputs, useErlangA, avgPatienceTime]
  );

  const updateBase = (field: keyof ErlangCInputs, value: string) => {
    const num = parseFloat(value);
    if (!isNaN(num) && num >= 0) {
      setBaseInputs((prev) => ({ ...prev, [field]: num }));
    }
  };

  const updateInterval = (idx: number, calls: number) => {
    setIntervals((prev) => {
      const next = [...prev];
      next[idx] = { ...next[idx], calls };
      return next;
    });
  };

  const handleReset = () => setIntervals([...DEFAULT_INTERVALS]);

  const handleDownload = () => {
    const model = useErlangA ? 'Erlang A' : 'Erlang C';
    const report = generateDayPlanReport(summary, model);
    downloadReport(report, `day-planner-${model.replace(' ', '-').toLowerCase()}.txt`);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Settings */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Settings</h3>
          <InputField label="AHT (seconds)" value={baseInputs.ahtSeconds} onChange={(v) => updateBase('ahtSeconds', v)} />
          <InputField label="Interval Length (min)" value={baseInputs.intervalMinutes} onChange={(v) => updateBase('intervalMinutes', v)} />
          <InputField label="Target SL %" value={baseInputs.targetSLPercent} onChange={(v) => updateBase('targetSLPercent', v)} />
          <InputField label="Target Answer Time (s)" value={baseInputs.targetAnswerTime} onChange={(v) => updateBase('targetAnswerTime', v)} />
          <InputField label="Shrinkage %" value={baseInputs.shrinkagePercent} onChange={(v) => updateBase('shrinkagePercent', v)} />
          <InputField label="Cost per Agent $/hr" value={baseInputs.costPerAgentHour} onChange={(v) => updateBase('costPerAgentHour', v)} />

          <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
            <input
              type="checkbox"
              checked={useErlangA}
              onChange={(e) => setUseErlangA(e.target.checked)}
              className="rounded border-gray-600 accent-cyan-500"
            />
            Use Erlang A model
          </label>

          {useErlangA && (
            <InputField
              label="Avg Patience Time (s)"
              value={avgPatienceTime}
              onChange={(v) => {
                const n = parseFloat(v);
                if (!isNaN(n) && n > 0) setAvgPatienceTime(n);
              }}
            />
          )}
        </div>

        {/* Interval Table + Chart */}
        <div className="lg:col-span-3 space-y-6">
          {/* Summary Cards */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
            <MetricCard label="Peak Raw" value={summary.peakRawAgents} highlight />
            <MetricCard label="Peak Rostered" value={summary.peakRosteredAgents} highlight />
            <MetricCard label="Avg Raw" value={summary.avgRawAgents.toFixed(1)} />
            <MetricCard label="Total Agent-Hours" value={summary.totalAgentHours.toFixed(1)} />
            <MetricCard label="Total Cost" value={'$' + summary.totalCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} highlight />
          </div>

          {/* Stacked Area Chart */}
          <div className="bg-navy-950/50 border border-gray-700/50 rounded-lg p-4">
            <Line
              data={{
                labels: summary.intervals.map((r) => r.time),
                datasets: [
                  {
                    label: 'Rostered Agents',
                    data: summary.intervals.map((r) => r.rosteredAgents),
                    borderColor: '#3b82f6',
                    backgroundColor: 'rgba(59, 130, 246, 0.15)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 3,
                    borderWidth: 2,
                  },
                  {
                    label: 'Raw Agents',
                    data: summary.intervals.map((r) => r.rawAgents),
                    borderColor: '#22d3ee',
                    backgroundColor: 'rgba(245, 158, 11, 0.15)',
                    fill: true,
                    tension: 0.3,
                    pointRadius: 3,
                    borderWidth: 2,
                  },
                ],
              }}
              options={{
                responsive: true,
                interaction: { intersect: false, mode: 'index' },
                plugins: {
                  title: { display: true, text: 'Staffing Profile Across the Day', color: '#e5e5e5', font: { size: 14, weight: 'bold' } },
                  legend: { labels: { color: '#9ca3af' } },
                },
                scales: {
                  x: { title: { display: true, text: 'Time', color: '#9ca3af' }, ticks: { color: '#6b7280' }, grid: { color: 'rgba(75, 85, 99, 0.2)' } },
                  y: { title: { display: true, text: 'Agents', color: '#9ca3af' }, ticks: { color: '#6b7280' }, grid: { color: 'rgba(75, 85, 99, 0.2)' }, beginAtZero: true },
                },
              }}
            />
          </div>

          {/* Interval Table */}
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-gray-400 uppercase tracking-wider">Interval Detail</h3>
            <div className="flex gap-2">
              <button
                onClick={handleReset}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors"
              >
                <RotateCcw size={14} /> Reset
              </button>
              <button
                onClick={handleDownload}
                className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 rounded px-3 py-1.5 transition-colors"
              >
                <Download size={14} /> Download
              </button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-400 uppercase tracking-wider border-b border-gray-700/50">
                  <th className="text-left py-2 px-2">Time</th>
                  <th className="text-right py-2 px-2">Calls</th>
                  <th className="text-right py-2 px-2">Erlangs</th>
                  <th className="text-right py-2 px-2">Raw</th>
                  <th className="text-right py-2 px-2">Rostered</th>
                  <th className="text-right py-2 px-2">SL%</th>
                  <th className="text-right py-2 px-2">ASA</th>
                  <th className="text-right py-2 px-2">Occ%</th>
                </tr>
              </thead>
              <tbody>
                {summary.intervals.map((r, i) => (
                  <tr key={r.time} className="border-b border-gray-800/30 hover:bg-navy-900/30">
                    <td className="py-1.5 px-2 text-gray-300 font-mono text-xs">{r.time}</td>
                    <td className="py-1.5 px-2 text-right">
                      <input
                        type="number"
                        value={intervals[i].calls}
                        onChange={(e) => updateInterval(i, parseInt(e.target.value) || 0)}
                        className="w-16 bg-transparent text-right text-white border-b border-gray-700/50 focus:border-blue-500 focus:outline-none"
                      />
                    </td>
                    <td className="py-1.5 px-2 text-right text-gray-400">{r.trafficIntensity.toFixed(1)}</td>
                    <td className="py-1.5 px-2 text-right text-blue-400 font-semibold">{r.rawAgents}</td>
                    <td className="py-1.5 px-2 text-right text-cyan-400 font-semibold">{r.rosteredAgents}</td>
                    <td className="py-1.5 px-2 text-right text-emerald-400">{(r.serviceLevel * 100).toFixed(1)}%</td>
                    <td className="py-1.5 px-2 text-right text-gray-300">{r.asa.toFixed(1)}s</td>
                    <td className={`py-1.5 px-2 text-right ${r.occupancy > 0.88 ? 'text-cyan-400' : 'text-gray-300'}`}>{(r.occupancy * 100).toFixed(1)}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
